import React, { useRef, useEffect } from 'react'
import Chart, { ChartOptions } from 'chart.js'
import { t } from '@lingui/macro'
import _ from 'lodash'
import applyRoundedRectangleFeature from './chartjsRoundedRectangle'

// @ts-expect-error
Chart.defaults.global.animation.duration = 0
Chart.defaults.global.tooltips.intersect = false

export interface IChartJSProps {
  style?: React.HTMLAttributes<HTMLCanvasElement>['style']
  width?: number
  height?: number
  config: Chart.ChartConfiguration
  enhanceMaxValue?: boolean
}

const defaultOpts = (maximumValue, enhanceMaxValue): ChartOptions => ({
  legend: {
    display: false
  },
  scales: {
    yAxes: [
      {
        ticks: {
          suggestedMin: 0,
          stepSize: maximumValue < 8 ? 1 : undefined, // this is to prevent charts to show decimal numbers on the axis if the values are low
          beginAtZero: true,
          max: Math.round(maximumValue * (enhanceMaxValue ? 1.2 : 1))
        }
      }
    ],
    xAxes: [
      {
        ticks: {
          fontColor: '#637A85'
        }
      }
    ]
  }
})

export const PieChartJS = ({ style, width, height, config }: IChartJSProps) => {
  const canvasElement = useRef(null)
  useEffect(() => {
    // @ts-expect-error 9/11/2020
    new Chart(canvasElement.current, config)
  })
  return (
    <canvas style={style} ref={canvasElement} width={width} height={height} />
  )
}

/**
 * keep in mind that this component works well only for a single dataset
 */
const ChartJS = ({
  style,
  width,
  height,
  config,
  enhanceMaxValue = true
}: IChartJSProps) => {
  applyRoundedRectangleFeature(Chart)
  const canvasElement = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    if (!config.options) {
      config.options = {}
    }
    // @ts-expect-error 9/11/2020
    const { chartInstance } = canvasElement.current
    // @ts-expect-error 9/11/2020
    const { datasets } = config.data
    let maximumValue = 0

    // deeply comparing chartInstance.data is not going to work because chart.js is adding some _meta props there, so we go deeper
    datasets.forEach((dataset, index) => {
      const { data } = dataset
      // @ts-expect-error 9/11/2020
      maximumValue = _.max(data) > maximumValue ? _.max(data) : maximumValue
      if (
        chartInstance &&
        _.isEqual(data, chartInstance.data.datasets[index].data)
      ) {
        return
      }
    })

    _.merge(config.options, defaultOpts(maximumValue, enhanceMaxValue))

    // correct update
    if (chartInstance) {
      chartInstance.destroy()
    }
    // if we reinitiliaze a chartJS on this element it wil animate the graph again, which is undesirable in all places where this component is used
    // @ts-expect-error 9/11/2020
    canvasElement.current.chartInstance = new Chart(
      // @ts-expect-error 9/11/2020
      canvasElement.current,
      config
    )
  })

  Chart.plugins.register({
    afterRender: function (chart) {
      // @ts-expect-error 9/11/2020
      if (chart.data.noData) {
        // @ts-expect-error 9/11/2020
        const ctx = chart.chart.ctx
        // @ts-expect-error 9/11/2020
        const width = chart.chart.width
        // @ts-expect-error 9/11/2020
        const height = chart.chart.height
        ctx.save()
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        ctx.font = "15px 'Helvetica Neue'"
        ctx.fillStyle = '#B1BFC5'
        ctx.fillText(t`No data to show in chart`, width / 2, height / 2 - 24)
        ctx.restore()
      }
    }
  })

  return (
    <canvas style={style} ref={canvasElement} width={width} height={height} />
  )
}

export default ChartJS
